import { getContentImageProps } from '@/lib/utils';
import ExternalLink from '@/components/elements/ExternalLink';
import { getLinkTargetData } from '@/utils/getLinkTargetData';

/**
 * Known as block b in Contentstack data. Has a large image and some floating text.
 */
export default function ImageBlock({ settings }) {
  const mobileImage = settings.background_image_mobile;
  const desktopImage = settings.background_image_desktop;
  const { srcSet: desktopSrcSet } = getContentImageProps({
    image: desktopImage,
    priority: true,
    fetchPriority: 'high',
    sizes: '(min-width: 1700px) 1128px, (min-width: 1024px) 60vw, 1128px'
  });
  const { srcSet: mobileSrcSet, remainingProps } = getContentImageProps({
    image: mobileImage,
    priority: true,
    fetchPriority: 'high',
    sizes: '100vw'
  });
  const linkData = getLinkTargetData(settings);

  if (!linkData) return null;

  const { linkText: CTAText, linkUrl: CTAUrl, linkType: CTAType } = linkData;

  return (
    <div className="block-b relative mt-5 size-full lg:mt-0 lg:ml-[20px]">
      <picture className="block">
        <source media="(min-width: 640px)" srcSet={desktopSrcSet} />
        <source srcSet={mobileSrcSet} />
        <img
          {...remainingProps}
          style={{ objectFit: 'cover', aspectRatio: 3 / 2 }}
          className="size-full max-h-[460px] min-h-[460px] sm:max-h-none sm:min-h-0 lg:max-h-[617px] lg:min-h-[617px] 2xl:max-h-[756px] 2xl:min-h-[756px]"
          alt={
            desktopImage?.description ??
            desktopImage?.title.replace(/\.[^/.]+$/, '')
          }
        />
      </picture>
      <div className="absolute top-15 right-0 max-w-[240px] min-w-full text-center sm:top-10 sm:right-10 sm:min-w-[240px] sm:text-right lg:top-15 lg:right-15">
        <div className="justify-center px-4 sm:px-0">
          <p
            className={`preheading-s pb-2 uppercase sm:pb-0 ${settings.light_text ? 'text-cozy-cognac-20' : 'text-promo-red'}`}
          >
            {settings.preheading}
          </p>
          <h2
            className={`h5 lg:h4 font-lustria ${settings.light_text ? 'text-white' : 'text-black'}`}
          >
            {settings.heading}
          </h2>
          {CTAText && CTAUrl && (
            <div className="mt-5 flex w-full justify-center sm:justify-end">
              <ExternalLink
                linkStyling={`h6 lg:h5 flex items-center`}
                internalLink={CTAType === 'internal' ? CTAUrl : undefined}
                externalLink={CTAType === 'external' ? CTAUrl : undefined}
              >
                <GoArrow light={settings.light_text} />
                <span
                  className={`txt-btn-secondary ${settings.light_text ? 'txt-btn-secondary-white text-white' : 'txt-btn-secondary text-black'}`}
                >
                  {CTAText}
                </span>
              </ExternalLink>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const GoArrow = ({ light }) => {
  return (
    <svg
      enableBackground="new 0 0 24 24"
      height="24"
      width="24"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="mr-[10px] self-center"
      aria-hidden="true"
    >
      <g
        fill="none"
        stroke={light ? '#FFFFFF' : '#000000'}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m3 12h18" />
        <path d="m16 7 5 5-5 5" />
      </g>
    </svg>
  );
};
